import type { Project } from "../types";

/**
 * Portfolio projects — single source of truth for the Portfolio section.
 *
 * To add a new project: append a new object to the array below. The
 * <Portfolio /> component will automatically render a new ProjectCard.
 *
 * Field reference (see src/types.ts for the canonical shape):
 *   - id:          unique stable string (used as React key)
 *   - title:       short project name
 *   - description: 1–3 sentences for the card body
 *   - thumbnail:   path under /public/projects (optional — placeholder used otherwise)
 *   - githubUrl:   link to the repo
 *   - liveUrl:     optional — only renders the "Live Site" button when present
 *   - techStack:   optional list of short tech labels rendered as stat chips
 *   - genre:       retained on the type but no longer rendered in the UI (kept so it can be reintroduced later)
 *   - rating:      retained on the type but no longer rendered in the UI (kept so it can be reintroduced later)
 *   - icon:        optional path under /public to a square project icon — falls back to a per-genre pixel-art placeholder
 */
export const PROJECTS: ReadonlyArray<Project> = [
  {
    id: "documentAssistant",
    title: "AI Document Assistant",
    description: "Privacy-first document assistant with zero-retention architecture. Documents are processed but never stored on servers. All persistent data lives in the user's browser (EntityDB). Backend is stateless.",
    icon: "/images/documentAssistant.png",
    githubUrl: "https://github.com/Resilient-Labs/multilingual-ai-document-assistant",
    techStack: ["TypeScript", "Next.js", "TailwindCSS", "EntityDB", "Hugging Face", "NLLB-200", "Tesseract.js", "Coqui TTS", "LangSmith"],
    liveUrl: "https://multilingual-ai-document-assistant-sigma.vercel.app",
  },
  {
    id: "huddleup",
    title: "HuddleUp",
    description:
      "A real-time collaboration application that allows users to communicate one-on-one or in groups via messaging and video chat.",
    icon: "/images/huddleup.png",
    githubUrl: "https://github.com/aidan-diaz/huddleup",
    techStack: ["React", "Node", "Convex", "Clerk", "LiveKit"],
    liveUrl: "https://huddle-up-xi.vercel.app",
  },
  {
    id: "nanochat",
    title: "nanochat",
    description:
      "A self-hosted, ChatGPT-style web app built on top of Karpathy's nanochat, featuring a small language model. Trained end-to-end from scratch on Nebius cloud GPUs.",
    icon: "/images/nanochat.png",
    githubUrl: "https://github.com/aidan-diaz/nanochat-nebius",
    techStack: ["Python", "PyTorch", "Flask"],
  },
  {
    id: "cookbookcorner",
    title: "Cookbook Corner",
    description:
      "A full-stack recipe management application where users can create, organize, and manage their personal cookbook and grocery lists.",
    icon: "/images/cookbookcorner.png",
    githubUrl: "https://github.com/aidan-diaz/cookbook-corner",
    techStack: ["React", "TailwindCSS", "Node", "Express", "MongoDB", "Clerk",],
    liveUrl: "https://cookbook-corner.onrender.com",
  },
  {
    id: "neuroflow",
    title: "NeuroFlow",
    description:
      "a full-stack web application that empowers users to improve and maintain their cognitive health through engaging tests and a personalized progress-tracking profile chart.",
    icon: "/images/neuroFlowThumbnail.png",
    githubUrl: "https://github.com/aidan-diaz/neuroflow",
    techStack: ["EJS", "CSS", "Express", "Passport", "Node", "D3.js", "MongoDB"],
    liveUrl: "https://neuroflow.onrender.com/",
  },
  {
    id: "careerclutch",
    title: "CareerClutch",
    description:
      "A hitlisting application that allows users to keep track of companies that they are looking to apply to during their job hunt.",
    icon: "/images/careerClutchThumbnail.png",
    githubUrl: "https://github.com/aidan-diaz/careerclutch",
    techStack: ["React", "TailwindCSS", "JSON Server"],
    liveUrl: "https://career-clutch.netlify.app/",
  },
  {
    id: "trickytrivia",
    title: "TrickyTrivia",
    description:
      "A category-based trivia game that lets users test their knowledge across boardgames, film, music, sports, and video games using questions pulled live from the Open Trivia DB API.",
    icon: "/images/triviaThumbnail.png",
    githubUrl: "https://github.com/aidan-diaz/trickytrivia",
    techStack: ["HTML", "CSS", "JavaScript"],
    liveUrl: "https://trickytrivia.netlify.app/",
  },
];
